import { CheckIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Flex,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  MenuItem,
  ModalBody,
  ModalFooter,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { IconChainLink4 } from 'central-icons/IconChainLink4';
import { useEffect, useRef, useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';

import { ActionModal } from '@revelio/core';

import { SharedReportRateLimitError } from '../../api/dash-meta-rate-limit-error';
import { createSharedReport } from '../../api/shared-reports-api';
import { usePageConfig } from '../PageConfigProvider/PageConfigProvider';
import { usePageState } from '../PageState/PageStateProvider';
import { useCoverSelectedParameters } from '../Reports/useCoverSelectedParameters';
import { useSharedReportData } from './SharedReportDataContext';

interface ShareReportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ShareReportModal = ({
  isOpen,
  onClose,
}: ShareReportModalProps) => {
  const { config } = usePageConfig();
  const { pageState } = usePageState();
  const sharedReportData = useSharedReportData();
  const coverParameters = useCoverSelectedParameters();
  const toast = useToast();

  const [isLoading, setIsLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isOpen) {
      setShareUrl('');
      setCopied(false);
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  const handleGenerateLink = async () => {
    if (!sharedReportData) return;

    setIsLoading(true);
    try {
      const chartData = sharedReportData.getAllChartData();

      const snapshotConfig = JSON.parse(JSON.stringify(config));
      // Bake resolved cover page parameters
      snapshotConfig._resolvedCoverParameters = coverParameters;
      for (const tab of snapshotConfig.tabs ?? []) {
        for (const item of tab.tabContent?.items ?? []) {
          if (!item.plot) continue;

          const registered = chartData[item.id];
          const markdown = registered?.markdown;
          const hasReplotComponent = Boolean(registered?.component);
          const isTextChart = item.plot.chartType === 'text';

          if (hasReplotComponent) {
            // Replots HOC registered component + props; viewer renders live
            // via StaticChartRenderer. Nothing to bake into plot config.
          } else if (markdown != null) {
            // Text / AI-summary charts register their resolved markdown so the
            // viewer renders via MarkdownRenderer without any data fetching.
            if (!isTextChart) {
              item.plot._originalChartType = item.plot.chartType;
              item.plot.chartType = 'text';
            }
            item.plot.textHtml = markdown;
          }

          if (registered?.title != null) {
            item.title = registered.title;
          }
        }
      }

      const { token } = await createSharedReport({
        name: config.name,
        pageConfig: snapshotConfig as unknown as Record<string, unknown>,
        chartData: chartData as unknown as Record<string, unknown>,
        pageState: pageState as unknown as Record<string, unknown>,
      });

      setShareUrl(`${window.location.origin}/shared/${token}`);
    } catch (error) {
      const isRateLimited = error instanceof SharedReportRateLimitError;
      toast({
        position: 'top-right',
        title: isRateLimited
          ? 'Too many share links generated'
          : 'Failed to generate share link',
        description: isRateLimited
          ? 'Please try again later.'
          : 'Please try again.',
        status: isRateLimited ? 'warning' : 'error',
        duration: isRateLimited ? 5000 : 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = () => {
    if (copyTimeoutRef.current) {
      clearTimeout(copyTimeoutRef.current);
    }
    setCopied(true);
    copyTimeoutRef.current = setTimeout(() => {
      setCopied(false);
      copyTimeoutRef.current = null;
    }, 2000);
  };

  return (
    <ActionModal header="Share report" isOpen={isOpen} onClose={onClose}>
      <ModalBody>
        <Flex direction="column" rowGap="1rem">
          <Stack spacing={1}>
            <Text fontSize="sm">
              Anyone with this link can view a snapshot of this report
            </Text>
            <InputGroup size="md">
              <Input
                pr={shareUrl ? '6rem' : '7.5rem'}
                value={shareUrl}
                placeholder="No shared link"
                disabled={!shareUrl}
                readOnly
              />
              <InputRightElement width={shareUrl ? '6rem' : '7.5rem'}>
                {shareUrl ? (
                  <CopyToClipboard text={shareUrl} onCopy={handleCopy}>
                    <Button
                      h="1.75rem"
                      size="sm"
                      colorScheme="green"
                      variant="solid"
                    >
                      {copied ? (
                        <>
                          <CheckIcon mr={1} /> Copied
                        </>
                      ) : (
                        'Copy Link'
                      )}
                    </Button>
                  </CopyToClipboard>
                ) : (
                  <Button
                    h="1.75rem"
                    size="sm"
                    colorScheme="green"
                    variant="solid"
                    isLoading={isLoading}
                    onClick={handleGenerateLink}
                  >
                    Generate Link
                  </Button>
                )}
              </InputRightElement>
            </InputGroup>
            {shareUrl && (
              <Box pt={1}>
                <Link
                  fontSize="sm"
                  textDecoration="underline"
                  _hover={{ textDecoration: 'none' }}
                  cursor="pointer"
                  href={shareUrl}
                  isExternal
                  target="_blank"
                  rel="noreferrer"
                  style={{ display: 'inline-flex' }}
                >
                  Preview link in new tab
                </Link>
              </Box>
            )}
          </Stack>
        </Flex>
      </ModalBody>
      <ModalFooter />
    </ActionModal>
  );
};

export const ShareReportMenuItem = () => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <MenuItem
        onClick={() => setIsOpen(true)}
        icon={<IconChainLink4 size={16} />}
        title="Share"
      >
        Share
      </MenuItem>
      <ShareReportModal isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </>
  );
};
